import React, { useEffect, useState } from 'react';
import { Trophy, AlertTriangle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ShoppingItem } from '../../types';
import { formatRelativeTime } from '../../src/lib/utils';

const money = (symbol: string, value: number) => `${symbol}${value.toFixed(2)}`;

// "12,50", "12.50", "1,299.00" and "1.299,00" all work: the last dot or comma
// is the decimal mark, any earlier ones are thousands separators.
const parsePrice = (raw: string) => {
  const cut = Math.max(raw.lastIndexOf('.'), raw.lastIndexOf(','));
  if (cut === -1) return parseFloat(raw);
  return parseFloat(`${raw.slice(0, cut).replace(/[.,]/g, '')}.${raw.slice(cut + 1)}`);
};

// How fresh and how backed a Lattice price is, in place of a blanket "Live":
// its age (green within a day, amber within a week, grey after) and, when the
// database reports it, how many shoppers reported it at that store.
export const LatticeFreshnessBadge: React.FC<{ date?: string | null; reports?: number | null }> = ({ date, reports }) => {
  const { t } = useTranslation();
  if (!date && reports == null) return null;
  const ageHours = date ? (Date.now() - new Date(date).getTime()) / 36e5 : null;
  const tone = ageHours == null ? 'text-gray-500 bg-gray-100/70' : ageHours < 24 ? 'text-emerald-600 bg-emerald-100/50' : ageHours < 24 * 7 ? 'text-amber-600 bg-amber-100/60' : 'text-gray-500 bg-gray-100/70';
  const dot = ageHours != null && ageHours < 24 ? 'bg-emerald-500' : ageHours != null && ageHours < 24 * 7 ? 'bg-amber-500' : 'bg-gray-400';
  const parts = [
    date ? formatRelativeTime(date, t) : null,
    reports != null ? t('lattice_status.reports', { count: reports, defaultValue: '{{count}} reports' }) : null,
  ].filter(Boolean);
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${tone}`}>
      <div className={`w-1 h-1 rounded-full ${dot}`} />
      <span className="text-[9px] font-black uppercase tracking-widest">{parts.join(' · ')}</span>
    </div>
  );
};

// Expanded line under a basket item whose price beats the Lattice.
export const LatticeBeaterPanel: React.FC<{ item: ShoppingItem; currencySymbol: string }> = ({ item, currencySymbol }) => {
  const { t } = useTranslation();
  const below = Math.max(0, (item.latticeRecord ?? item.price) - item.price);
  return (
    <div className="bg-emerald-50/60 rounded-2xl p-4 border border-emerald-100 mt-2 flex items-center gap-3">
      <div className="bg-emerald-500 p-1.5 rounded-lg text-white shrink-0">
        <Trophy size={14} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
          {t('history.lattice_beater', { defaultValue: 'Lattice-Beater' })}
        </p>
        <p className="text-sm font-bold text-[#1a2b3c]">
          {t('lattice_status.below_nearby', { defaultValue: '{{amount}} below nearby', amount: money(currencySymbol, below) })}
        </p>
      </div>
    </div>
  );
};

// "Usually €1.99 nearby. Is €129.99 right?" with a quick price/quantity fix.
export const PriceCheckPanel: React.FC<{
  item: ShoppingItem;
  currencySymbol: string;
  onUpdate: (id: string, updates: Partial<ShoppingItem>) => void;
}> = ({ item, currencySymbol, onUpdate }) => {
  const { t } = useTranslation();
  const [price, setPrice] = useState(String(item.price ?? ''));
  const [quantity, setQuantity] = useState(String(item.quantity ?? 1));
  useEffect(() => { setPrice(String(item.price ?? '')); }, [item.price]);
  useEffect(() => { setQuantity(String(item.quantity ?? 1)); }, [item.quantity]);

  const usual = item.priceCheck?.usualPrice ?? 0;
  const nextPrice = parsePrice(price);
  const nextQuantity = parseInt(quantity, 10);
  const valid = Number.isFinite(nextPrice) && nextPrice >= 0 && Number.isFinite(nextQuantity) && nextQuantity >= 1;
  const changed = valid && (nextPrice !== item.price || nextQuantity !== item.quantity);

  const save = () => {
    if (!changed) return;
    onUpdate(item.id, { price: nextPrice, quantity: nextQuantity });
  };

  return (
    <div className="bg-amber-50/70 rounded-2xl p-4 border border-amber-200/70 mt-2">
      <div className="flex items-start gap-3 mb-3">
        <div className="bg-amber-500 p-1.5 rounded-lg text-white shrink-0">
          <AlertTriangle size={14} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest">
            {t('lattice_status.check_price', { defaultValue: 'Check price' })}
          </p>
          <p className="text-sm font-bold text-[#1a2b3c] leading-snug">
            {t('lattice_status.usually_nearby', {
              defaultValue: 'Usually {{usual}} nearby. Is {{price}} right?',
              usual: money(currencySymbol, usual),
              price: money(currencySymbol, item.price || 0),
            })}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[6rem]">
          <span className="block text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1">
            {t('product.price_label', { defaultValue: 'Price' })}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={price}
            onChange={e => setPrice(e.target.value.replace(/[^0-9.,]/g, ''))}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            className="w-full bg-white border border-amber-200 rounded-xl px-3 py-2 text-sm font-bold text-[#1a2b3c] focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </label>
        <label className="w-16 shrink-0">
          <span className="block text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1">
            {t('product.qty_label', { defaultValue: 'Quantity' })}
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={quantity}
            onChange={e => setQuantity(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            className="w-full bg-white border border-amber-200 rounded-xl px-3 py-2 text-sm font-bold text-[#1a2b3c] focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={!changed}
          className="shrink-0 max-[359px]:w-full flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40 transition-opacity"
        >
          <Check size={12} strokeWidth={3} />
          {t('common.save', { defaultValue: 'Save' })}
        </button>
      </div>
    </div>
  );
};
